import { useEffect, useRef, useState } from 'react';
import { Undo2 } from 'lucide-react';
import { notify } from '@/lib/toast';

/** How long the toast stays up (audit T6); Ctrl/⌘+Z keeps working after it has gone. */
export const UNDO_TOAST_MS = 8000;
const MODAL_OPEN = '[role="dialog"][aria-modal="true"]';

/**
 * The offer's words (audit T6), inside its toast. The toast hides itself after 8 s — held while the pointer or the
 * focus is on it, or while a dialog covers it (time under a dialog does not count: the person could not see it).
 * Keyed by the offer, so a new action starts a fresh 8 s.
 */
export function UndoMessage({ toastId, text }: { toastId: string; text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [held, setHeld] = useState(false);
  useEffect(() => {
    const toast = ref.current?.closest<HTMLElement>('[data-sonner-toast]');
    if (!toast) return undefined;
    const hold = () => setHeld(true);
    const release = () => setHeld(false);
    const blur = (event: FocusEvent) => { if (!toast.contains(event.relatedTarget as Node | null)) release(); };
    toast.addEventListener('pointerenter', hold);
    toast.addEventListener('pointerleave', release);
    toast.addEventListener('focusin', hold);
    toast.addEventListener('focusout', blur);
    return () => {
      toast.removeEventListener('pointerenter', hold);
      toast.removeEventListener('pointerleave', release);
      toast.removeEventListener('focusin', hold);
      toast.removeEventListener('focusout', blur);
    };
  }, []);
  useEffect(() => {
    if (held) return undefined;
    let timer = 0;
    const arm = () => {
      timer = window.setTimeout(() => {
        // An open dialog holds the toast, except Hana's phone sheet (D142): it is a side panel, not a question.
        const modal = document.querySelector(MODAL_OPEN);
        if (modal !== null && modal.querySelector('[data-assistant-panel]') === null) arm();
        else notify.dismiss(toastId);
      }, UNDO_TOAST_MS);
    };
    arm();
    return () => window.clearTimeout(timer);
  }, [held, toastId]);
  return <span ref={ref} data-undo="">{text}</span>;
}

/**
 * Shows (or replaces in place) the one undo toast of `toastId`: its words, and a `Hoàn tác` button that also names the
 * ⌘Z shortcut. `key` changes with each offer so its 8 s start again.
 */
export function showUndoToast({ toastId, text, key, onUndo }: { toastId: string; text: string; key: number;
  onUndo: () => void }): void {
  notify.message(<UndoMessage key={key} toastId={toastId} text={text} />, {
    id: toastId,
    // The toast keeps its own time (`UndoMessage`): held while pointed at or focused, and while a dialog covers it.
    duration: Infinity,
    icon: <Undo2 aria-hidden="true" />,
    action: (
      <button type="button" data-button="" aria-keyshortcuts="Control+Z Meta+Z" onClick={onUndo}>
        Hoàn tác<kbd aria-hidden="true">(Ctrl+Z)</kbd>
      </button>
    ),
  });
}
